import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { BetSlipProvider } from './context/BetSlipContext';
import { Header, MobileNav } from './components/Navigation';
import { BetSlipDrawer } from './components/BetSlipDrawer';
import { ProtectedRoute, AdminRoute, PublicOnlyRoute } from './components/ProtectedRoute';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { TooltipProvider } from './components/ui/tooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pages
import HomePage from './pages/HomePage';
import RacesPage from './pages/RacesPage';
import MeetingPage from './pages/MeetingPage';
import RaceDetailPage from './pages/RaceDetailPage';
import HorseProfilePage from './pages/HorseProfilePage';
import StablePage from './pages/StablePage';
import SettingsPage from './pages/SettingsPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ResendVerificationPage from './pages/ResendVerificationPage';
import CompetitionsPage from './pages/CompetitionsPage';
import CompetitionDetailPage from './pages/CompetitionDetailPage';
import AdminCompetitionsPage from './pages/AdminCompetitionsPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminAISettingsPage from './pages/AdminAISettingsPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminPredictionsPage from './pages/AdminPredictionsPage';
import AdminEngagementPage from './pages/AdminEngagementPage';
import ExpertsVsAIPage from './pages/ExpertsVsAIPage';
import BetHistoryPage from './pages/BetHistoryPage';
import PredictionsPage from './pages/PredictionsPage';
import FootballCompetitionsPage from './pages/FootballCompetitionsPage';
import FootballCompetitionDetailPage from './pages/FootballCompetitionDetailPage';
import FootballHubPage from './pages/FootballHubPage';
import RacingHubPage from './pages/RacingHubPage';
import MatchDetailPage from './pages/MatchDetailPage';
import TeamPage from './pages/TeamPage';
import AdminFootballPage from './pages/AdminFootballPage';
import AdminFootballEngagementPage from './pages/AdminFootballEngagementPage';
import AdminWCMappingsPage from './pages/AdminWCMappingsPage';
import AdminWCPlayerMappingsPage from './pages/AdminWCPlayerMappingsPage';
import AdminWCMappingReviewPage from './pages/AdminWCMappingReviewPage';
import AdminStatsApiMappingsPage from './pages/AdminStatsApiMappingsPage';
import FootballJoinPage from './pages/FootballJoinPage';
import FestivalJoinPage from './pages/FestivalJoinPage';
import WorldCupDisplayPage from './pages/WorldCupDisplayPage';
import WC2026StatsPage from './pages/WC2026StatsPage';

import './App.css';

// Component that checks if home page is enabled and redirects if not
function HomePageOrRedirect() {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await axios.get(`${API_URL}/api/site-settings/public`);
                setSettings(res.data);
            } catch (err) {
                console.error('Failed to fetch settings:', err);
                setSettings({ feature_toggles: { home_page: true } });
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, []);

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
    }

    // If home page is disabled, redirect to the configured default page
    if (settings?.feature_toggles?.home_page === false) {
        const destination = settings?.default_page || '/football-hub';
        return <Navigate to={destination} replace />;
    }

    return <HomePage />;
}

function AppLayout({ children }) {
    return (
        <div className="min-h-screen bg-background">
            <Header />
            <main className="pb-20 md:pb-0">{children}</main>
            <MobileNav />
            <BetSlipDrawer />
        </div>
    );
}

function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <BetSlipProvider>
                    <TooltipProvider delayDuration={200}>
                    <BrowserRouter>
                        <Routes>
                            {/* Public auth routes - redirect to home if already logged in */}
                            <Route path="/login" element={
                                <PublicOnlyRoute><LoginPage /></PublicOnlyRoute>
                            } />
                            <Route path="/register" element={
                                <PublicOnlyRoute><RegisterPage /></PublicOnlyRoute>
                            } />
                            <Route path="/forgot-password" element={
                                <PublicOnlyRoute><ForgotPasswordPage /></PublicOnlyRoute>
                            } />
                            <Route path="/reset-password" element={
                                <PublicOnlyRoute><ResetPasswordPage /></PublicOnlyRoute>
                            } />
                            <Route path="/verify-email/:token" element={
                                <VerifyEmailPage />
                            } />
                            <Route path="/resend-verification" element={
                                <ResendVerificationPage />
                            } />
                            
                            {/* Protected routes - require login */}
                            <Route path="/" element={
                                <ProtectedRoute>
                                    <AppLayout><HomePageOrRedirect /></AppLayout>
                                </ProtectedRoute>
                            } />
                            <Route path="/races" element={
                                <ProtectedRoute>
                                    <AppLayout><RacesPage /></AppLayout>
                                </ProtectedRoute>
                            } />
                            <Route path="/meeting/:stageId" element={
                                <ProtectedRoute>
                                    <AppLayout><MeetingPage /></AppLayout>
                                </ProtectedRoute>
                            } />
                            <Route path="/races/:stageId" element={
                                <ProtectedRoute>
                                    <AppLayout><RacesPage /></AppLayout>
                                </ProtectedRoute>
                            } />
                            <Route path="/race/:eventId" element={
                                <ProtectedRoute>
                                    <AppLayout><RaceDetailPage /></AppLayout>
                                </ProtectedRoute>
                            } />
                            {/* Exact RaceWise pages embedded by Clockwork. Authentication is
                                exchanged from a short-lived, path-scoped assertion. */}
                            <Route path="/embed/race/:eventId" element={
                                <ProtectedRoute><AppLayout><RaceDetailPage /></AppLayout></ProtectedRoute>
                            } />
                            <Route path="/horse/:horseId" element={
                                <ProtectedRoute>
                                    <AppLayout><HorseProfilePage /></AppLayout>
                                </ProtectedRoute>
                            } />
                            <Route path="/stable" element={
                                <ProtectedRoute>
                                    <AppLayout><StablePage /></AppLayout>
                                </ProtectedRoute>
                            } />
                            <Route path="/settings" element={
                                <ProtectedRoute>
                                    <AppLayout><SettingsPage /></AppLayout>
                                </ProtectedRoute>
                            } />
                            <Route path="/competitions" element={
                                <ProtectedRoute>
                                    <AppLayout><CompetitionsPage /></AppLayout>
                                </ProtectedRoute>
                            } />
                            <Route path="/competitions/join/:inviteCode" element={
                                <ProtectedRoute>
                                    <AppLayout><CompetitionsPage /></AppLayout>
                                </ProtectedRoute>
                            } />
                            <Route path="/competitions/:competitionId" element={
                                <ProtectedRoute>
                                    <AppLayout><CompetitionDetailPage /></AppLayout>
                                </ProtectedRoute>
                            } />
                            <Route path="/bets" element={
                                <ProtectedRoute>
                                    <AppLayout><BetHistoryPage /></AppLayout>
                                </ProtectedRoute>
                            } />
                            <Route path="/experts-vs-ai" element={
                                <ProtectedRoute>
                                    <AppLayout><ExpertsVsAIPage /></AppLayout>
                                </ProtectedRoute>
                            } />
                            <Route path="/predictions" element={
                                <ProtectedRoute>
                                    <AppLayout><PredictionsPage /></AppLayout>
                                </ProtectedRoute>
                            } />
                            <Route path="/football-hub" element={
                                <ProtectedRoute>
                                    <AppLayout><FootballHubPage /></AppLayout>
                                </ProtectedRoute>
                            } />
                            <Route path="/racing-hub" element={
                                <ProtectedRoute>
                                    <AppLayout><RacingHubPage /></AppLayout>
                                </ProtectedRoute>
                            } />
                            <Route path="/football-hub/match/:matchId" element={
                                <ProtectedRoute>
                                    <AppLayout><MatchDetailPage /></AppLayout>
                                </ProtectedRoute>
                            } />
                            <Route path="/embed/football-hub/match/:matchId" element={
                                <ProtectedRoute><AppLayout><MatchDetailPage /></AppLayout></ProtectedRoute>
                            } />
                            <Route path="/football-hub/team/:teamId" element={
                                <ProtectedRoute>
                                    <AppLayout><TeamPage /></AppLayout>
                                </ProtectedRoute>
                            } />
                            <Route path="/football/join/:inviteCode" element={
                                <AppLayout><FootballJoinPage /></AppLayout>
                            } />
                            <Route path="/festival/join/:inviteCode" element={<FestivalJoinPage />} />
                            <Route path="/football" element={
                                <ProtectedRoute>
                                    <AppLayout><FootballCompetitionsPage /></AppLayout>
                                </ProtectedRoute>
                            } />
                            <Route path="/football/:compId" element={
                                <ProtectedRoute>
                                    <AppLayout><FootballCompetitionDetailPage /></AppLayout>
                                </ProtectedRoute>
                            } />

                            {/* World Cup display — full-screen, no app layout, ProtectedRoute only */}
                            <Route path="/wc2026" element={
                                <ProtectedRoute><WorldCupDisplayPage /></ProtectedRoute>
                            } />
                            {/* WC stats fun-facts — no AppLayout, touch-screen card page */}
                            <Route path="/wc2026/stats" element={
                                <ProtectedRoute><WC2026StatsPage /></ProtectedRoute>
                            } />
                            {/* WC match detail — no AppLayout so the kiosk nav never appears */}
                            <Route path="/wc2026/match/:matchId" element={
                                <ProtectedRoute><MatchDetailPage /></ProtectedRoute>
                            } />
                            {/* WC team page — no AppLayout so the kiosk nav never appears */}
                            <Route path="/wc2026/team/:teamId" element={
                                <ProtectedRoute><TeamPage /></ProtectedRoute>
                            } />

                            {/* Admin routes - require admin role */}
                            <Route path="/admin/football" element={
                                <AdminRoute>
                                    <AppLayout><AdminFootballPage /></AppLayout>
                                </AdminRoute>
                            } />
                            <Route path="/admin/football/engagement" element={
                                <AdminRoute>
                                    <AppLayout><AdminFootballEngagementPage /></AppLayout>
                                </AdminRoute>
                            } />
                            <Route path="/admin/worldcup/mappings" element={
                                <AdminRoute>
                                    <AppLayout><AdminWCMappingsPage /></AppLayout>
                                </AdminRoute>
                            } />
                            <Route path="/admin/worldcup/player-mappings" element={
                                <AdminRoute>
                                    <AppLayout><AdminWCPlayerMappingsPage /></AppLayout>
                                </AdminRoute>
                            } />
                            <Route path="/admin/worldcup/mapping-review" element={
                                <AdminRoute>
                                    <AppLayout><AdminWCMappingReviewPage /></AppLayout>
                                </AdminRoute>
                            } />
                            <Route path="/admin/statsapi/mappings" element={
                                <AdminRoute>
                                    <AppLayout><AdminStatsApiMappingsPage /></AppLayout>
                                </AdminRoute>
                            } />
                            <Route path="/admin/football/engagement/:compId" element={
                                <AdminRoute>
                                    <AppLayout><AdminFootballEngagementPage /></AppLayout>
                                </AdminRoute>
                            } />
                            <Route path="/admin/competitions" element={
                                <AdminRoute>
                                    <AppLayout><AdminCompetitionsPage /></AppLayout>
                                </AdminRoute>
                            } />
                            <Route path="/admin/users" element={
                                <AdminRoute>
                                    <AppLayout><AdminUsersPage /></AppLayout>
                                </AdminRoute>
                            } />
                            <Route path="/admin/ai-settings" element={
                                <AdminRoute>
                                    <AppLayout><AdminAISettingsPage /></AppLayout>
                                </AdminRoute>
                            } />
                            <Route path="/admin/dashboard" element={
                                <AdminRoute>
                                    <AppLayout><AdminDashboardPage /></AppLayout>
                                </AdminRoute>
                            } />
                            <Route path="/admin/predictions" element={
                                <AdminRoute>
                                    <AppLayout><AdminPredictionsPage /></AppLayout>
                                </AdminRoute>
                            } />
                            <Route path="/admin/engagement" element={
                                <ProtectedRoute>
                                    <AppLayout><AdminEngagementPage /></AppLayout>
                                </ProtectedRoute>
                            } />
                            <Route path="/admin/engagement/:competitionId" element={
                                <ProtectedRoute>
                                    <AppLayout><AdminEngagementPage /></AppLayout>
                                </ProtectedRoute>
                            } />
                        </Routes>
                    </BrowserRouter>
                    </TooltipProvider>
                    <Toaster
                        position="top-right" 
                        richColors 
                        closeButton
                        theme="system"
                    />
                </BetSlipProvider>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
